import { isNullOrUndefined, isPrimitive } from './types.utils';

/* tslint:disable-next-line quotemark */
const entityMap: string[][] = [
	['&', '&amp;'],
	['<', '&lt;'],
	['>', '&gt;'],
	['"', '&quot;'],
	["'", '&#39;'],
];

/**
 * Encodes certain characters to their html entity equivalent
 * @param str The string to encode
 */
export function encode(str: string = ''): string {
	entityMap.forEach((map: string[]) => {
		const [key, value] = map;
		str = str.replace(new RegExp(key, 'g'), value);
	});

	return str;
}

/**
 * Changes certain html entities to their normal string equivalent
 * @param str The string to decode
 */
export function decode(str: string = ''): string {
	entityMap.forEach((map: string[]) => {
		const [value, key] = map;
		str = str.replace(new RegExp(key, 'g'), value);
	});

	return str;
}

/**
 * Converts a javascript object to XML.
 */
export function jsonToXml(json: object): string {
	if (isNullOrUndefined(json)) {
		return '';
	}

	let rootNode;

	// parse information
	Object.keys(json).forEach((key) => {
		const value: any = json[key];

		if (typeof value === 'object') {
			rootNode = { key, value };
		}
	});

	// write information
	if (!isNullOrUndefined(rootNode)) {
		return jsonChildrenToXML(rootNode.value, rootNode.key);
	}
}

/**
 * Converts an XMLDocument or String to a javascript object.
 */
export function xmlToJson(xml: XMLDocument | string): object {
	if (isNullOrUndefined(xml)) {
		return;
	}

	if (typeof xml === 'string') {
		xml = stringToXML(xml);
	}

	// Create the return object
	let obj;
	let i;
	let j;
	let item;
	let childNodeName;
	let convertedItem;

	if (xml instanceof Element) {
		// ELEMENT
		obj = {};

		// get attributes on element
		for (i = 0, j = xml.attributes.length; i < j; i++) {
			const { nodeName, value } = xml.attributes.item(i);
			obj[nodeName] = value;
		}

		// get child nodes
		if (xml.hasChildNodes()) {
			for (i = 0, j = xml.childNodes.length; i < j; i++) {
				item = xml.childNodes[i];
				childNodeName = item.nodeName === '#text' ? '$t' : item.nodeName;
				convertedItem = xmlToJson(item);
				if (isNullOrUndefined(convertedItem)) {
					continue;
				}
				if (typeof obj[childNodeName] === 'undefined') {
					obj[childNodeName] = childNodeName === '$t' ? convertedItem : [convertedItem];
				} else if (obj[childNodeName].constructor !== Array) {
					obj[childNodeName] = [obj[childNodeName], convertedItem];
				} else {
					obj[childNodeName].push(convertedItem);
				}
			}
		}
	} else if (xml.nodeType === 3 || xml.nodeType === 4) {
		// TEXT || CDATA
		obj = xml.nodeValue;
	} else if (xml.nodeType === 9) {
		// DOCUMENT
		obj = {};

		if (xml.hasChildNodes()) {
			// should only have 1 child
			item = xml.childNodes[0];
			convertedItem = xmlToJson(item);
			if (!isNullOrUndefined(convertedItem)) {
				obj[item.nodeName] = convertedItem;
			}
		}
	}

	// do children

	return obj;
}

/**
 * Converts a string to an XMLDocument.
 */
export function stringToXML(oString: string): XMLDocument {
	return new DOMParser().parseFromString(oString, 'text/xml');
}

/**
 * Converts an XMLDocument to a string.
 */
export function xmlToString(xml: XMLDocument): string {
	return new XMLSerializer().serializeToString(xml);
}

/**
 * Converts a javascript object child node into XML.
 * @param json The object to convert
 * @param nodeName javascript object key name
 * @returns XML string
 */
export function jsonChildrenToXML(json: object, nodeName: string): string {
	let returnXML: string;
	let innerText = '';
	const attributes = {};
	const childNodes = [];

	Object.keys(json).forEach((key) => {
		const value = json[key];
		if (isNullOrUndefined(value)) {
			return;
		} else if (key === '$t') {
			innerText = value;
		} else if (isPrimitive(value)) {
			attributes[key] = value.toString();
		} else if (Array.isArray(value)) {
			Object.keys(value).forEach((subKey) => {
				const subValue = value[subKey];

				if (isNullOrUndefined(subValue)) {
					return;
				} else if (isPrimitive(subValue)) {
					attributes[key] = subValue.toString();
				} else if (typeof subValue === 'object') {
					childNodes.push({ key, value: subValue });
				}
			});
		} else if (typeof value === 'object') {
			childNodes.push({ key, value });
		}
	});

	returnXML = `<${nodeName}`;

	// ATTRIBUTES
	Object.keys(attributes).forEach((attr) => {
		if (attr !== '$$hashKey') {
			returnXML += ` ${attr}="${encode(attributes[attr])}"`;
		}
	});

	returnXML += `>${innerText}`;

	// CHILD NODES
	Object.keys(childNodes).forEach((child) => {
		returnXML += jsonChildrenToXML(childNodes[child].value, childNodes[child].key);
	});

	returnXML += `</${nodeName}>`;

	return returnXML;
}
